import { formatDate } from "@/lib/format"
import type { Sprint } from "@/types/sprint-report"

interface Props {
  sprint: Sprint
  previousSprint: Sprint
  nextSprint: Sprint
}

/** Sprint names may carry a decoration, e.g. "Sprint 89 - Public Launch 🚀".
 *  Show just the base name ("Sprint 89") — the part before the first " - ". */
const cleanSprintName = (name: string) => name.split(" - ")[0].trim()

export function ReportHeader({ sprint, previousSprint, nextSprint }: Props) {
  return (
    <header className="mb-5 animate-rise">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="mb-1.5 flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-win opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-win" />
            </span>
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-headerSub">
              CAT-OS
            </span>
          </div>
          <h1 className="font-display text-3xl font-extrabold leading-none tracking-tight text-ink">
            Bug Status Report
          </h1>
          <p className="mt-2 text-xs text-slate-500">
            Current sprint: <span className="font-semibold text-ink">{cleanSprintName(sprint.name)}</span>
            {sprint.start && sprint.end && (
              <span className="num text-slate-400">
                {" · "}
                {formatDate(sprint.start)} → {formatDate(sprint.end)}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <SprintChip label="Previous" value={cleanSprintName(previousSprint.name)} />
          <span className="text-slate-300">·</span>
          <SprintChip label="Current" value={cleanSprintName(sprint.name)} active />
          <span className="text-slate-300">·</span>
          <SprintChip label="Next" value={cleanSprintName(nextSprint.name)} />
        </div>
      </div>
    </header>
  )
}

function SprintChip({
  label,
  value,
  active = false,
}: {
  label: string
  value: string
  active?: boolean
}) {
  return (
    <div
      className={
        active
          ? "rounded-lg bg-ink px-2.5 py-1 text-white shadow-card"
          : "rounded-lg border border-slate-200 bg-white/70 px-2.5 py-1 text-slate-600"
      }
    >
      <div className="text-[9px] font-semibold uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-xs font-semibold leading-tight">{value}</div>
    </div>
  )
}
